import { useEffect, useMemo, useState } from 'react'
import { Modal } from '../Modal'
import { labelRefeicao, type RefeicaoFicha } from '../../lib/fichaTecnicaPacote'
import type { SecaoDescricaoInput } from '../../lib/descricaoCardapioFicha'
import { descricaoCardapioDaSecao, secaoTemConteudo } from '../../lib/descricaoCardapioFicha'
import type { AlimentoMacronutriente, Prato } from '../../types/database'

export type AplicarCardapioModo = 'dia' | 'lote'

export type AplicarRefeicaoEscolha = {
  refeicao: RefeicaoFicha
  pratoId: string | null
}

type Props = {
  open: boolean
  modo: AplicarCardapioModo
  alvoLabel: string
  turnoLabel: string
  secoes: Array<SecaoDescricaoInput & { refeicao: RefeicaoFicha }>
  pratos: Prato[]
  alimentosById: Map<string, AlimentoMacronutriente>
  applying: boolean
  onClose: () => void
  onConfirm: (escolhas: AplicarRefeicaoEscolha[]) => void
}

export function ModalAplicarCardapio({
  open,
  modo,
  alvoLabel,
  turnoLabel,
  secoes,
  pratos,
  alimentosById,
  applying,
  onClose,
  onConfirm,
}: Props) {
  const comConteudo = useMemo(
    () => secoes.filter((s) => secaoTemConteudo(s)),
    [secoes],
  )

  const [checked, setChecked] = useState<Set<RefeicaoFicha>>(new Set())
  const [pratoPorRefeicao, setPratoPorRefeicao] = useState<Partial<Record<RefeicaoFicha, string>>>(
    {},
  )

  useEffect(() => {
    if (!open) return
    setChecked(new Set(comConteudo.map((s) => s.refeicao)))
    const initial: Partial<Record<RefeicaoFicha, string>> = {}
    for (const s of comConteudo) {
      if (s.pratoIds.length === 1) initial[s.refeicao] = s.pratoIds[0]
    }
    setPratoPorRefeicao(initial)
  }, [open, comConteudo])

  function toggle(ref: RefeicaoFicha) {
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(ref)) next.delete(ref)
      else next.add(ref)
      return next
    })
  }

  const faltandoPrato = useMemo(() => {
    for (const s of comConteudo) {
      if (!checked.has(s.refeicao)) continue
      if (s.pratoIds.length > 1 && !pratoPorRefeicao[s.refeicao]) return true
      if (s.pratoIds.length === 1 && !pratoPorRefeicao[s.refeicao]) return true
    }
    return false
  }, [checked, comConteudo, pratoPorRefeicao])

  function handleConfirm() {
    const escolhas: AplicarRefeicaoEscolha[] = []
    for (const s of comConteudo) {
      if (!checked.has(s.refeicao)) continue
      const pratoId =
        s.pratoIds.length > 0 ? pratoPorRefeicao[s.refeicao] || s.pratoIds[0] || null : null
      if (s.pratoIds.length > 0 && !pratoId) continue
      escolhas.push({ refeicao: s.refeicao, pratoId })
    }
    onConfirm(escolhas)
  }

  const title = modo === 'lote' ? 'Aplicar pacote nos dias selecionados' : 'Aplicar pacote neste dia'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn-secondary" disabled={applying} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={applying || checked.size === 0 || faltandoPrato}
            onClick={handleConfirm}
          >
            {applying ? 'Aplicando…' : 'Confirmar aplicação'}
          </button>
        </>
      }
    >
      <p className="kpi-sub" style={{ marginTop: 0 }}>
        Turno <strong>{turnoLabel}</strong> · destino: <strong>{alvoLabel}</strong>
      </p>
      <p className="kpi-sub">
        Escolha a refeição e o <strong>prato específico</strong> que será aplicado neste(s) dia(s).
        Somente o prato selecionado entra no Pedido mensal.
      </p>
      {!comConteudo.length ? (
        <p className="kpi-sub">Nenhuma refeição do pacote tem conteúdo para aplicar.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {comConteudo.map((s) => {
            const desc = descricaoCardapioDaSecao(s, pratos, alimentosById)
            const pratosSecao = s.pratoIds
              .map((id) => pratos.find((p) => p.id === id))
              .filter(Boolean) as Prato[]
            return (
              <div
                key={s.refeicao}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  padding: 10,
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  background: '#fff',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked.has(s.refeicao)}
                  onChange={() => toggle(s.refeicao)}
                  style={{ marginTop: 3 }}
                  aria-label={labelRefeicao(s.refeicao)}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{labelRefeicao(s.refeicao)}</div>
                  <div
                    className="kpi-sub"
                    style={{ fontSize: 12, marginBottom: pratosSecao.length ? 8 : 0 }}
                  >
                    {desc || 'Sem modo de preparo'}
                  </div>
                  {checked.has(s.refeicao) && pratosSecao.length > 0 && (
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label>Prato para este dia</label>
                      <select
                        value={pratoPorRefeicao[s.refeicao] || ''}
                        onChange={(e) =>
                          setPratoPorRefeicao((prev) => ({
                            ...prev,
                            [s.refeicao]: e.target.value,
                          }))
                        }
                        disabled={applying}
                      >
                        <option value="">Selecionar prato…</option>
                        {pratosSecao.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {checked.has(s.refeicao) && pratosSecao.length === 0 && (
                    <div className="kpi-sub" style={{ fontSize: 12 }}>
                      Sem pratos vinculados — será aplicada a descrição/alimentos manuais da ficha.
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </Modal>
  )
}
